import type { CheckpointState } from '@ofmos/shared/checklist';
import { RECORD_QUERY_MAX_LIMIT } from '@ofmos/shared/record-query';
import { useMemo } from 'react';
import type { FieldMetadata, ObjectMetadata, RecordRow } from '../../../data/client';
import { useFields, useListedRecords, useObjects, useRecordList } from '../../../data/hooks';
import { fieldsByName, objectByName } from '../../dashboard/queries';
import { textOf } from '../../dashboard/rowValues';
import { todayKey } from '../../record/dueDate';
import {
  compareBoardRows,
  dayCount,
  dayLine,
  isOnBoard,
  lastTickedLabel,
  mostLateDays,
  type NextStep,
  nextStepOf,
  onboardingSteps,
  orderTeam,
  progressOf,
  stepStates,
  WELCOME_FIELD_NAME,
} from './onboardingModel';

/**
 * THE BOARD'S DATA: enrollments, their agencies, the team - three reads, resolved by api name.
 *
 * ONE HOP IS NOT ENOUGH HERE, which is why the agencies are their own read rather than the
 * enrollment's relation label: the owner is the AGENCY's csm, and the churned test is the
 * agency's status (the row budget in `dashboard/queries.ts` explains why a relation only
 * carries a label). Each read is one page at the query maximum (500); the live instance holds
 * 111 enrollments and 190 agencies, so one page is the whole set. The rows land in their
 * canonical cache entries and are read back from there through one cache subscription per list
 * (`useListedRecords`), which is what makes a tick or an owner change on this screen repaint the
 * row at once (INV-13) without an observer, and a refetch, per record.
 */

export interface TeamMember {
  readonly id: string;
  readonly name: string;
}

export interface OnboardingRow {
  readonly enrollment: RecordRow;
  readonly agencyId: string | null;
  readonly name: string;
  readonly ownerId: string | null;
  readonly contractStart: string | null;
  readonly dayLine: string;
  /** "Day 9" without the last step (the redesigned page's client column and cards). */
  readonly day: string;
  readonly next: NextStep | null;
  readonly states: readonly CheckpointState[];
  readonly progress: { readonly done: number; readonly total: number };
  readonly lateDays: number;
}

export interface OnboardingBoard {
  /**
   * `error` when a read the board needs failed (DESIGN.md H7: a failed load is never shown as an
   * empty list); `retry` asks the failed reads again.
   */
  readonly status: 'loading' | 'absent' | 'error' | 'ready';
  readonly retry: () => void;
  readonly enrollmentObject: ObjectMetadata | undefined;
  readonly agencyObject: ObjectMetadata | undefined;
  readonly enrollmentFields: readonly FieldMetadata[];
  readonly steps: readonly FieldMetadata[];
  /** The agency's `csm` field, or undefined when the installation has none. */
  readonly csmField: FieldMetadata | undefined;
  readonly contractStartField: FieldMetadata | undefined;
  readonly welcomeField: FieldMetadata | undefined;
  readonly rows: readonly OnboardingRow[];
  readonly team: readonly TeamMember[];
}

const NO_IDS: readonly string[] = [];

function select(fields: ReadonlyMap<string, FieldMetadata>, names: readonly string[]) {
  return names.flatMap((name) => {
    const field = fields.get(name);
    return field === undefined ? [] : [field.id];
  });
}

function optionLabel(field: FieldMetadata | undefined, row: RecordRow): string | null {
  if (field === undefined) return null;
  const value = row.values[field.id];
  if (typeof value !== 'string') return null;
  return field.options?.selectOptions?.find((option) => option.id === value)?.label ?? null;
}

function relationId(field: FieldMetadata | undefined, row: RecordRow): string | null {
  if (field === undefined) return null;
  const value = row.values[field.id];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function useOnboardingBoard(): OnboardingBoard {
  const objects = useObjects();
  const enrollmentObject = objectByName(objects.data, 'cohort_enrollment');
  const agencyObject = objectByName(objects.data, 'agency');
  const teamObject = objectByName(objects.data, 'team_member');

  const enrollmentFieldsQuery = useFields(
    enrollmentObject?.id ?? '',
    enrollmentObject !== undefined,
  );
  const agencyFieldsQuery = useFields(agencyObject?.id ?? '', agencyObject !== undefined);
  const teamFieldsQuery = useFields(teamObject?.id ?? '', teamObject !== undefined);

  const enrollmentFields = enrollmentFieldsQuery.data ?? [];
  const eFields = useMemo(
    () => fieldsByName(enrollmentFieldsQuery.data),
    [enrollmentFieldsQuery.data],
  );
  const aFields = useMemo(() => fieldsByName(agencyFieldsQuery.data), [agencyFieldsQuery.data]);
  const tFields = useMemo(() => fieldsByName(teamFieldsQuery.data), [teamFieldsQuery.data]);
  const steps = useMemo(
    () => onboardingSteps(enrollmentFieldsQuery.data ?? []),
    [enrollmentFieldsQuery.data],
  );

  const enrollmentSelect = [
    ...select(eFields, [
      'name',
      'agency',
      'phase',
      'enrollment_status',
      'contract_start_date',
      WELCOME_FIELD_NAME,
    ]),
    ...steps.map((field) => field.id),
  ];
  const enrollmentIds = useRecordList(enrollmentObject?.id ?? '', {
    select: enrollmentSelect,
    limit: RECORD_QUERY_MAX_LIMIT,
    enabled: enrollmentObject !== undefined && enrollmentFieldsQuery.isSuccess,
  });
  const agencyIds = useRecordList(agencyObject?.id ?? '', {
    select: select(aFields, ['name', 'csm', 'lifecycle_status']),
    limit: RECORD_QUERY_MAX_LIMIT,
    enabled: agencyObject !== undefined && agencyFieldsQuery.isSuccess,
  });
  const teamIds = useRecordList(teamObject?.id ?? '', {
    select: select(tFields, ['name', 'is_active']),
    limit: RECORD_QUERY_MAX_LIMIT,
    enabled: teamObject !== undefined && teamFieldsQuery.isSuccess,
  });

  /* One cache subscription per list, not one observer per record (BR-PERF-1): this hook is
     mounted on every page through the sidebar's Onboarding count, so per-record observers here
     refetched every enrollment, agency and team member on each change notice of those objects. */
  const enrollments = useListedRecords(
    enrollmentObject?.id ?? '',
    enrollmentIds.data?.ids ?? NO_IDS,
    enrollmentIds,
  );
  const agencies = useListedRecords(
    agencyObject?.id ?? '',
    agencyIds.data?.ids ?? NO_IDS,
    agencyIds,
  );
  const members = useListedRecords(teamObject?.id ?? '', teamIds.data?.ids ?? NO_IDS, teamIds);

  const rows = useMemo<readonly OnboardingRow[]>(() => {
    const today = todayKey();
    const agencyById = new Map(agencies.records.map((row) => [row.id, row]));
    const agencyField = eFields.get('agency');
    const startField = eFields.get('contract_start_date');
    const built: OnboardingRow[] = [];
    for (const enrollment of enrollments.records) {
      if (enrollment.deletedAt !== null) continue;
      const agencyId = relationId(agencyField, enrollment);
      const agency = agencyId === null ? undefined : agencyById.get(agencyId);
      const churned =
        (agency !== undefined &&
          optionLabel(aFields.get('lifecycle_status'), agency) === 'Churned') ||
        optionLabel(eFields.get('enrollment_status'), enrollment) === 'Withdrawn';
      const states = stepStates(steps, enrollment.values, today);
      const progress = progressOf(states);
      const contractStart = textOf(enrollment, startField?.id);
      if (
        !isOnBoard(
          {
            phaseLabel: optionLabel(eFields.get('phase'), enrollment),
            contractStart,
            churned,
            progress,
          },
          today,
        )
      ) {
        continue;
      }
      const agencyName = agency === undefined ? null : textOf(agency, aFields.get('name')?.id);
      const relationLabel =
        agencyField === undefined ? undefined : enrollment.relationLabels?.[agencyField.id];
      const name =
        agencyName ??
        relationLabel ??
        textOf(enrollment, eFields.get('name')?.id) ??
        'Unnamed client';
      built.push({
        enrollment,
        agencyId: agency === undefined ? null : agency.id,
        name,
        ownerId: agency === undefined ? null : relationId(aFields.get('csm'), agency),
        contractStart,
        dayLine: dayLine(contractStart, lastTickedLabel(steps, states), today),
        day: dayCount(contractStart, today),
        next: nextStepOf(steps, enrollment.values, today),
        states,
        progress,
        lateDays: mostLateDays(states),
      });
    }
    return built.sort(compareBoardRows);
  }, [enrollments.records, agencies.records, eFields, aFields, steps]);

  /** The active team for the owner popup, in the owner menu's order (`orderTeam`). */
  const team = useMemo<readonly TeamMember[]>(() => {
    const nameField = tFields.get('name');
    const activeField = tFields.get('is_active');
    const active = members.records
      .filter((row) => activeField === undefined || row.values[activeField.id] !== false)
      .map((row) => ({ id: row.id, name: textOf(row, nameField?.id) ?? 'Unnamed' }));
    return orderTeam(active, rows);
  }, [members.records, tFields, rows]);

  const loading =
    objects.isPending ||
    (enrollmentObject !== undefined &&
      (enrollmentFieldsQuery.isPending || enrollmentIds.isPending || enrollments.isPending)) ||
    (agencyObject !== undefined && (agencyIds.isPending || agencies.isPending)) ||
    (teamObject !== undefined && (teamIds.isPending || members.isPending));

  const reads = [
    objects,
    enrollmentFieldsQuery,
    agencyFieldsQuery,
    teamFieldsQuery,
    enrollmentIds,
    agencyIds,
    teamIds,
  ];
  const failed = reads.filter((read) => read.isError);
  const retry = () => {
    for (const read of failed) void read.refetch();
  };

  return {
    status:
      failed.length > 0
        ? 'error'
        : loading
          ? 'loading'
          : enrollmentObject === undefined
            ? 'absent'
            : 'ready',
    retry,
    enrollmentObject,
    agencyObject,
    enrollmentFields,
    steps,
    csmField: aFields.get('csm'),
    contractStartField: eFields.get('contract_start_date'),
    welcomeField: eFields.get(WELCOME_FIELD_NAME),
    rows,
    team,
  };
}
